import type { Emitter } from "@core/events/emitter";

/** Identifier of an active editor tool. M9 will replace this string union
 *  with the broader Unity tool catalogue. */
export type ToolId =
  | "select"
  | "laser"
  | "comment"
  | "slice"
  | "sketch"
  | "measure"
  | "grab";

export type TransformMode = "translate" | "rotate" | "scale";
export type TransformSpace = "world" | "local";
export type SelectionMode = "model" | "part";

export interface ToolDefinition {
  readonly id: ToolId;
  readonly label: string;
  readonly icon: string;
  readonly shortcut?: string;
}

/**
 * Editor tool palette — floats at the bottom-right of the viewport in the
 * Mac UI. These are general editor tools (unrelated to the Spark AI
 * prompt). Order matches `Content/Gizmos/ToolMenu/SharedToolsMenu.prefab`.
 */
export const TOOLS: ReadonlyArray<ToolDefinition> = [
  { id: "select", label: "Select", icon: "cursor", shortcut: "Q" },
  { id: "laser", label: "Laser", icon: "laser" },
  { id: "comment", label: "Comment", icon: "comment" },
  { id: "slice", label: "Slice", icon: "slice" },
  { id: "sketch", label: "Sketch", icon: "sketch" },
  { id: "measure", label: "Measure", icon: "measure" },
  { id: "grab", label: "Grab", icon: "grab" },
];

/**
 * `IToolbarModel` — minimal surface backing the editor `ToolsMenuView`
 * pill and `HeaderView` invoke buttons. The full tool catalogue (slice,
 * bbox, presenter tools, …) lands in M9 alongside the per-tool models;
 * this interface stays the seam ViewModels talk to.
 *
 * Unity references (no exact 1:1 — the desktop app has multiple tool
 * models): `Models/Tools/BaseToolsModel.cs`,
 * `Models/Tools/PresenterToolsModel.cs`,
 * `ViewModels/Toolbar/ToolsMenuViewModel.cs`.
 */
export interface IToolbarModel {
  readonly onActiveToolChanged: Emitter<ToolId>;
  readonly onTransformModeChanged: Emitter<TransformMode>;
  readonly onTransformSpaceChanged: Emitter<TransformSpace>;
  readonly onSelectionModeChanged: Emitter<SelectionMode>;
  readonly onCommandInvoked: Emitter<string>;

  readonly activeTool: ToolId;
  readonly transformMode: TransformMode;
  readonly transformSpace: TransformSpace;
  readonly selectionMode: SelectionMode;

  setActiveTool(id: ToolId): void;
  setTransformMode(mode: TransformMode): void;
  setTransformSpace(space: TransformSpace): void;
  setSelectionMode(mode: SelectionMode): void;
  invoke(command: string): void;
}

export const TOOLBAR_MODEL = Symbol.for("Campfire.ToolbarModel");
